import { useState, useMemo } from 'react';
import { demoCanteenItems } from '@/mocks/demoData';
import type { CanteenItem } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';

const CATEGORIES = ['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Beverages', 'Special'];

export default function CanteenMenu() {
  const { addToast } = useApp();
  const [items, setItems] = useState<CanteenItem[]>(demoCanteenItems);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<CanteenItem | null>(null);
  const [formData, setFormData] = useState({ name: '', category: 'Breakfast', price: '', gst_percent: '5', available: true });

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const q = search.toLowerCase();
      if (search && !i.name.toLowerCase().includes(q)) return false;
      if (activeCategory !== 'all' && i.category !== activeCategory) return false;
      return true;
    });
  }, [items, search, activeCategory]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach((i) => { counts[i.category] = (counts[i.category] || 0) + 1; });
    return counts;
  }, [items]);

  const openAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', category: 'Breakfast', price: '', gst_percent: '5', available: true });
    setShowForm(true);
  };

  const openEdit = (item: CanteenItem) => {
    setEditingItem(item);
    setFormData({ name: item.name, category: item.category, price: String(item.price), gst_percent: String(item.gst_percent), available: item.available });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.price) { addToast('error', 'Name and price are required'); return; }
    if (editingItem) {
      setItems((prev) => prev.map((i) => i.id === editingItem.id ? { ...i, ...formData, price: Number(formData.price), gst_percent: Number(formData.gst_percent) } : i));
      addToast('success', 'Menu item updated');
    } else {
      setItems((prev) => [...prev, { id: `cnt-${Date.now()}`, ...formData, price: Number(formData.price), gst_percent: Number(formData.gst_percent), created_at: new Date().toISOString() }]);
      addToast('success', 'Menu item added');
    }
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this menu item?')) { setItems((prev) => prev.filter((i) => i.id !== id)); addToast('success', 'Item deleted'); }
  };

  const toggleAvailable = (item: CanteenItem) => {
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, available: !i.available } : i));
    addToast('success', `${item.name} is now ${!item.available ? 'available' : 'unavailable'}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Canteen Menu</h1>
          <p className="text-navy-500 mt-1">Manage food items, categories, pricing, and availability.</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Add Item
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Total Items</div>
          <div className="text-2xl font-bold text-navy-800">{items.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Available</div>
          <div className="text-2xl font-bold text-green-600">{items.filter((i) => i.available).length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Unavailable</div>
          <div className="text-2xl font-bold text-red-600">{items.filter((i) => !i.available).length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Categories</div>
          <div className="text-2xl font-bold text-navy-800">{CATEGORIES.length}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <input type="text" placeholder="Search menu items..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setActiveCategory('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${activeCategory === 'all' ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}>
              All ({items.length})
            </button>
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setActiveCategory(c)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeCategory === c ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}>
                {c} ({categoryCounts[c] || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((item) => (
          <div key={item.id} className={`bg-white rounded-xl border overflow-hidden transition-all ${item.available ? 'border-navy-100 hover:shadow-md' : 'border-slate-200 opacity-75'}`}>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.available ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                  {item.available ? 'Available' : 'Unavailable'}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(item)} className="p-1 text-navy-500 hover:bg-navy-50 rounded transition-colors cursor-pointer"><i className="ri-edit-line" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                </div>
              </div>
              <h3 className="font-semibold text-navy-800 mb-1">{item.name}</h3>
              <div className="text-xs text-navy-500 mb-3">{item.category}</div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-bold text-navy-800">Rs. {item.price.toLocaleString('en-IN')}</div>
                  <div className="text-xs text-navy-400">GST {item.gst_percent}%</div>
                </div>
                <button
                  onClick={() => toggleAvailable(item)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${item.available ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                >
                  {item.available ? 'Mark Unavailable' : 'Mark Available'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{editingItem ? 'Edit Item' : 'Add Menu Item'}</h3>
              <button onClick={() => setShowForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Item Name *</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Category</label><select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.category} onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Price (Rs.) *</label><input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.price} onChange={(e) => setFormData((p) => ({ ...p, price: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">GST %</label><input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.gst_percent} onChange={(e) => setFormData((p) => ({ ...p, gst_percent: e.target.value }))} /></div>
                <div className="flex items-center gap-3 pt-7">
                  <input type="checkbox" id="avail" checked={formData.available} onChange={(e) => setFormData((p) => ({ ...p, available: e.target.checked }))} className="w-4 h-4 accent-navy-700 cursor-pointer" />
                  <label htmlFor="avail" className="text-sm text-navy-700 cursor-pointer">Available</label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Save Item</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}